// @ts-nocheck

import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { MatchEntityCompetitors } from '../../states/entities/matches';
import type { ScoreboardV2Props } from '../../states/scoreboards';
import theme from '../../styled-components/dark';
import { MatchTitleAllowedSportIdsAnimation } from '../../types/match';
import { SportIds } from '../../types/sports';
import { pixelToEm } from '../../utils/common';
import AnimateOnChange from '../AnimateOnChange';
import FlagIcon from '../FlagIcon';
import MatchTitle from '../MatchTitle';
import ScoreboardScorers from '../ScoreboardScorers';
import ScrollView from '../ScrollView';

const ANIMATION_DURATION = 1800;

// Global
const Wrapper = styled.div`
  width: 100%;
  color: ${theme.Scoreboards.color};
  background: ${theme.Scoreboards.wrapperBackground};
  box-sizing: border-box;
  position: relative;
`;

const ContentWrapper = styled.div`
  font-size: ${Target.__MOBILE__ ? 0.87 : 0.85}em;
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  background: ${theme.Scoreboards.wrapperBackground};
  padding: 0 0.8em 0.3em;

  ${({ $noPadding }) =>
    $noPadding
      ? css`
          padding: 0;
        `
      : null}

  ${({ $alignCenter }) =>
    $alignCenter
      ? css`
          justify-content: center;
        `
      : null}
`;

const Column = styled.div`
  min-width: ${pixelToEm(27)}em;
`;

const RowSize = `${pixelToEm(21)}em`;

// Competitors
const Competitors = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-weight: ${({ $lightFont }) => ($lightFont ? 'normal' : 600)};
  flex-shrink: 1;
`;

const StyledFlagIcon = styled(FlagIcon)`
  height: 1.1em;
  width: 1.1em;
  margin-right: 0.3em;
  flex-shrink: 0;
`;

const Competitor = styled.div`
  font-size: ${pixelToEm(18)}em;
  display: flex;
  height: 50%;
  align-items: center;
  min-width: 0; // Needed to make ellipsis work
  box-sizing: border-box;
`;

const CompetitorsWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;

  ${({ $hideLabel }) =>
    $hideLabel
      ? css`
          height: 100%;
        `
      : css`
          height: calc(100% - ${pixelToEm(27)}em);
        `}
`;

const CompetitorEllipsisContainer = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ServerHeightWrapper = styled.span`
  font-size: ${pixelToEm(14)}em;
`;

const ServerIcon = styled.div`
  width: ${pixelToEm(6)}em;
  min-width: ${pixelToEm(6)}em;
  height: ${pixelToEm(6)}em;
  background: ${theme.Scoreboards.serverIconBackground};
  border-radius: ${pixelToEm(5)}em;
  margin-left: ${pixelToEm(5)}em;
`;

const HeaderHeightWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  height: ${pixelToEm(27)}em;
  align-items: center;
`;

// SetScores
const ScoresArea = styled.div`
  display: flex;
  flex-direction: ${({ $isOverflowing }) => ($isOverflowing ? 'row-reverse' : 'row')};
  flex: 1 0 auto;

  overflow: hidden;
  position: relative;
`;

const SetScoreArea = styled.div`
  z-index: 2;
  display: flex;
  position: relative;
  white-space: nowrap;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
`;

const SetScoresHeader = styled(Column)`
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  align-items: center;
  font-size: ${pixelToEm(13)}em;
  padding: ${({ $hasLabel, $isLongLabel }) => ($hasLabel || $isLongLabel ? '0 0.5em' : '0')};
  box-sizing: border-box;
  color: ${theme.Scoreboards.color};
`;

const HeaderEmWrapper = styled.span`
  height: ${pixelToEm(27)}em;

  width: 100%;
  ${({ $isFirstCol }) => {
    if ($isFirstCol) {
      return css`
        &:before {
          position: absolute;
          content: '';
          width: 100%;
          height: ${pixelToEm(27)}em;
        }
      `;
    }

    return null;
  }};
`;

const HeaderGameScoreEmWrapper = styled(HeaderEmWrapper)`
  z-index: 1;
`;

const SetScoreColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  color: ${theme.Scoreboards.setScoreColumn};

  ${({ $disableHighlightLastSet }) =>
    !$disableHighlightLastSet
      ? css`
          &:last-of-type {
            color: ${theme.Scoreboards.lastSetScoreColumn};
          }
        `
      : ''}
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${theme.Scoreboards.color};
  padding: 0.5em;
`;

const FlexOverflowWrapper = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  justify-content: space-between;
`;

// GameScores
const GameScoreColumnWrapper = styled.div`
  margin-left: 0.4em;
`;

const GameScoreColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding-left: 0.4em;
  box-sizing: border-box;
  min-width: ${pixelToEm(32)}em;

  ${({ $border }) =>
    $border
      ? css`
          border-left: 2px solid rgba(255, 255, 255, 0.2);
        `
      : ''};
`;

const GAME_SCORE_BORDER_RADIUS = `6px`;

const GameScoreWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: center;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  border-radius: ${GAME_SCORE_BORDER_RADIUS};
`;

// Correspond au score "en cours" (tout à droite)
const GameScoreRow = styled.div`
  display: flex;
  flex-direction: row;
  min-height: ${RowSize};
  width: 100%;
  justify-content: center;
  z-index: 2;
  color: ${theme.Scoreboards.gameScoreColor};

  &:first-child {
    border-radius: ${GAME_SCORE_BORDER_RADIUS} ${GAME_SCORE_BORDER_RADIUS} 0 0;
  }

  &:last-child {
    border-radius: 0 0 ${GAME_SCORE_BORDER_RADIUS} ${GAME_SCORE_BORDER_RADIUS};
  }

  ${({ $isBackgroundHighlighted, $light }) => css`
    background: ${!$isBackgroundHighlighted ? theme.Scoreboards.gameScoreRowBackground : 'transparent'};
    color: ${theme.Scoreboards[$light ? 'scoresColor' : 'gameScoreColor']};
  `}
`;

const GameScoresHeader = styled(SetScoresHeader)``;

const ScoreHeightWrapper = styled.span`
  font-size: ${pixelToEm(18)}em;
  width: 100%;

  ${({ $border, $light }) =>
    $border
      ? css`
          &::before {
            content: '';
            position: absolute;
            left: -0.5em;
            top: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.1);
            width: ${$light ? 1 : 2}px;
          }
        `
      : null}
`;

const Score = styled.div`
  min-height: ${RowSize};
  opacity: ${({ $fullOpacity }) => ($fullOpacity ? 1 : 0.6)};
  font-weight: ${({ $lightFont }) => ($lightFont ? 'normal' : 'bold')};
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ScoresWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const SetScoreBackgroundDivs = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const SetScoreBackgroundHeader = styled.div`
  width: 100%;
  height: ${pixelToEm(27)}em;
`;

const SetScoreBackgroundRow = styled.div`
  width: 100%;
  height: ${RowSize};
`;

const Wrap = styled.div`
  display: inline-block;
  position: relative;
  z-index: 3;
  height: 100%;
`;

const WrappedScrollView = styled(ScrollView)`
  width: 100%;
  display: flex;
  flex-direction: ${({ $isOverflowing }) => ($isOverflowing ? 'row' : 'row-reverse')};
`;

const ScorersWrapper = styled.div`
  padding: 0 0.8em;
`;

const ScoreboardSetsLayout = ({
  className,
  competitors,
  scores,
  defaultVisibleSetsNumber,
  expandThreshold = 1,
  disableHighlightLastSet = false,
  isLive,
  isCompact = false,
  sportId,
  isEnded,
  hideLabel = false,
  light = false,
  hideScorer = false,
  animatedScore = false,
}: ScoreboardV2Props) => {
  const { secondaries, main } = scores;
  const [isOverflowing, setIsOverflowing] = useState(false);

  const [displaySecondaries, setDisplaySecondaries] = useState(null);
  const [displayMain, setDisplayMain] = useState(null);

  const { label: mainLabel, score: mainScore } = displayMain ?? {};

  const scrollToLastSetScoreRef = useRef();
  const scrollViewWrapperRef = useRef();
  const setScoreWrapperRef = useRef();

  const hasAtLeastOneCompetitor = useMemo(
    () => MatchEntityCompetitors.hasAtLeastOneCompetitor(competitors),
    [competitors],
  );
  const hasAtLeastTwoCompetitors = useMemo(
    () => MatchEntityCompetitors.hasAtLeastTwoCompetitors(competitors),
    [competitors],
  );
  const hasScorer = useMemo(
    () => MatchEntityCompetitors.hasScorer(competitors, hasAtLeastOneCompetitor, hasAtLeastTwoCompetitors),
    [competitors, hasAtLeastOneCompetitor, hasAtLeastTwoCompetitors],
  );

  const isSpecialCompactLayout = useMemo(
    () =>
      isCompact && (sportId === SportIds.BASKETBALL || sportId === SportIds.ICEHOCKEY || sportId === SportIds.FOOTBALL),
    [isCompact, sportId],
  );

  // we filter the score to remove null, empty and undefined values
  // `hasScores` is used to avoid the display of an empty background when we do not have scores
  const hasScores = useMemo(
    () => mainScore?.filter((score) => score !== null && score !== '' && score !== '-').length > 0,
    [mainScore],
  );

  const isScoreWon = useCallback(
    (index, scoreIndex, setScore) =>
      (!disableHighlightLastSet && index === secondaries.length - 1 && isLive) ||
      setScore.score.every((pScore, i) => scoreIndex === i || parseInt(setScore.score[scoreIndex]) > parseInt(pScore)),
    [isLive, secondaries, disableHighlightLastSet],
  );

  const renderScorers = useCallback(
    (competitors) => {
      const scorersCompetitor1 = (hasAtLeastOneCompetitor && competitors[0].scorers) || [];
      const scorersCompetitor2 = (hasAtLeastTwoCompetitors && competitors[1].scorers) || [];

      return (
        <ScorersWrapper>
          <ScoreboardScorers
            scorersCompetitor1={scorersCompetitor1}
            scorersCompetitor2={scorersCompetitor2}
            expandThreshold={expandThreshold}
          />
        </ScorersWrapper>
      );
    },
    [hasAtLeastOneCompetitor, hasAtLeastTwoCompetitors, expandThreshold],
  );

  useEffect(() => {
    const setScoreWidth = setScoreWrapperRef?.current?.getBoundingClientRect().width;
    const scrollViewWidth = scrollViewWrapperRef?.current?.getBoundingClientRect().width;

    if (setScoreWidth && scrollViewWidth) {
      if (setScoreWidth >= scrollViewWidth) {
        setIsOverflowing(true);
      }
    }
  }, []);

  useEffect(() => {
    let timeout = null;

    if (displaySecondaries === null || !animatedScore) {
      setDisplaySecondaries(secondaries);
    }

    if (displayMain === null || !animatedScore) {
      setDisplayMain(main);
    }

    if (
      !animatedScore ||
      displaySecondaries === null ||
      displayMain === null ||
      secondaries === null ||
      main === null
    ) {
      return;
    }

    if (isEqual(main, displayMain)) {
      setDisplaySecondaries(secondaries);
      setDisplayMain(main);
    } else {
      timeout = setTimeout(() => {
        setDisplaySecondaries(secondaries);
        setDisplayMain(main);
      }, ANIMATION_DURATION);
    }

    return () => clearTimeout(timeout);
  }, [animatedScore, displayMain, displaySecondaries, main, secondaries]);

  return (
    <Wrapper className={className}>
      <ContentWrapper $isCompact={isCompact} $noPadding={light} $alignCenter={isSpecialCompactLayout}>
        {isSpecialCompactLayout ? (
          <Main>
            <MatchTitle
              competitor1Name={hasAtLeastOneCompetitor ? competitors[0].name : ''}
              competitor2Name={hasAtLeastTwoCompetitors ? competitors[1].name : ''}
              competitor1NbRedCards={hasAtLeastOneCompetitor ? competitors[0].nbRedCards : 0}
              competitor2NbRedCards={hasAtLeastTwoCompetitors ? competitors[1].nbRedCards : 0}
              score1={mainScore?.length >= 1 ? parseInt(mainScore[0], 10) : null}
              score2={mainScore?.length >= 2 ? parseInt(mainScore[1], 10) : null}
              flag1={hasAtLeastOneCompetitor ? competitors[0].flag : undefined}
              flag2={hasAtLeastTwoCompetitors ? competitors[1].flag : undefined}
              animatedScore={MatchTitleAllowedSportIdsAnimation.includes(sportId)}
              isFinalScore={isEnded}
            />
          </Main>
        ) : (
          <>
            <FlexOverflowWrapper>
              <Competitors $lightFont={light}>
                {!hideLabel ? <HeaderHeightWrapper /> : null}
                <CompetitorsWrapper $hideLabel={hideLabel}>
                  {competitors &&
                    competitors.map((competitor, index) => (
                      <Competitor key={index}>
                        {competitor.flag ? <StyledFlagIcon flagId={competitor.flag} /> : null}

                        <CompetitorEllipsisContainer>{competitor.name}</CompetitorEllipsisContainer>
                        {competitor.isServing ? (
                          <ServerHeightWrapper>
                            <ServerIcon />
                          </ServerHeightWrapper>
                        ) : null}
                      </Competitor>
                    ))}
                </CompetitorsWrapper>
              </Competitors>
              <ScoresArea $isOverflowing={isOverflowing}>
                <SetScoreBackgroundDivs>
                  <SetScoreBackgroundHeader />
                  <SetScoreBackgroundRow />
                </SetScoreBackgroundDivs>
                <WrappedScrollView
                  defaultVisibleSetsNumber={defaultVisibleSetsNumber}
                  vertical={false}
                  shouldScrollToElement={displaySecondaries?.length}
                  elementToScrollTo={scrollToLastSetScoreRef}
                  shouldDisplayScrollbar={false}
                  ref={scrollViewWrapperRef}
                  isOverflowing={isOverflowing}
                >
                  <Wrap ref={setScoreWrapperRef} defaultVisibleSetsNumber={defaultVisibleSetsNumber}>
                    <SetScoreArea isInfiniteSets={displaySecondaries?.length ?? 0 > 5}>
                      {displaySecondaries?.map((setScore, index) => (
                        <SetScoreColumn
                          key={index}
                          ref={scrollToLastSetScoreRef}
                          $disableHighlightLastSet={disableHighlightLastSet || !isLive}
                        >
                          {!hideLabel ? (
                            <HeaderEmWrapper $isFirstCol={index === 0}>
                              <SetScoresHeader $hasLabel={!!setScore.label}>
                                {setScore.label ? setScore.label : index + 1}
                              </SetScoresHeader>
                            </HeaderEmWrapper>
                          ) : null}
                          <ScoresWrapper>
                            {setScore?.score?.map((score, scoreIndex) => (
                              <React.Fragment key={scoreIndex}>
                                <ScoreHeightWrapper>
                                  <Score
                                    $isFirstCol={index === 0}
                                    $fullOpacity={isScoreWon(index, scoreIndex, setScore)}
                                    $lightFont={light}
                                  >
                                    {score}
                                  </Score>
                                </ScoreHeightWrapper>
                              </React.Fragment>
                            ))}
                          </ScoresWrapper>
                        </SetScoreColumn>
                      ))}
                    </SetScoreArea>
                  </Wrap>
                </WrappedScrollView>
              </ScoresArea>
            </FlexOverflowWrapper>
            {hasScores ? (
              <GameScoreColumnWrapper>
                <GameScoreColumn>
                  {!hideLabel ? (
                    <HeaderGameScoreEmWrapper>
                      <GameScoresHeader isLongLabel={(mainLabel || '').length > 3}>
                        {mainLabel || displaySecondaries?.length + 1 || 1}
                      </GameScoresHeader>
                    </HeaderGameScoreEmWrapper>
                  ) : null}
                  <GameScoreWrapper>
                    {mainScore?.map((gameScore, index) => (
                      <GameScoreRow key={index} $light={light}>
                        <ScoreHeightWrapper $border={!!displaySecondaries?.length} $light={light}>
                          <Score $fullOpacity $lightFont={light}>
                            <AnimateOnChange>{gameScore}</AnimateOnChange>
                          </Score>
                        </ScoreHeightWrapper>
                      </GameScoreRow>
                    ))}
                  </GameScoreWrapper>
                </GameScoreColumn>
              </GameScoreColumnWrapper>
            ) : null}
          </>
        )}
      </ContentWrapper>
      {!isCompact && hasScorer && !hideScorer && renderScorers(competitors)}
    </Wrapper>
  );
};

export default ScoreboardSetsLayout;
